import { api } from ".";

/**
 * Amount distribution entry - a single amount and its percentage
 */
export interface IAmountDistributionEntry {
  amount: number; // In cents
  percentage: number; // 0-1
}

/**
 * Distribution with optional range for highly variable data
 */
export interface IDistributionWithRange {
  entries: IAmountDistributionEntry[];
  sampleCount: number;
  // Range fields for highly variable distributions (when entries is empty)
  minAmount?: number; // In cents
  maxAmount?: number; // In cents
}

/**
 * Charge amount distribution (global across all payers)
 */
export type IChargeAmountDistribution = IDistributionWithRange;

/**
 * Payer-specific amount distributions
 */
export interface IPayerAmountDistributions {
  payerName: string;
  paidAmount: IDistributionWithRange;
  patientOop: IDistributionWithRange;
}

/**
 * Interface for implant suggestion response
 */
export interface IImplantSuggestion {
  implantCptCode: string;
  associationPercentage: number;
  source: "CASE_SIGNATURE" | "CPT_CODE";
  sourceValue: string;
  // Global charge distribution (across all payers)
  chargeDistribution: IChargeAmountDistribution | null;
  // Payer-specific distributions (if payerId provided)
  payerDistributions: IPayerAmountDistributions | null;
}

export interface IImplantSuggestionsResponse {
  suggestion: IImplantSuggestion | null;
}

export interface IImplantSuggestionsRequest {
  caseSignature?: string;
  cptCodes?: string[];
  payerId?: number;
  accountId?: string;
}

/**
 * RTK Query API slice for implant suggestions endpoint
 * Provides hook for fetching implant code suggestions based on case signatures or CPT codes
 */
const implantSuggestionsQueries = api.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Get implant suggestion
     * Returns the best matching implant code suggestion based on historical 835 data
     */
    getImplantSuggestion: builder.query<
      IImplantSuggestionsResponse,
      IImplantSuggestionsRequest
    >({
      query: (params) => {
        const queryParams: Record<string, string> = {};

        if (params.caseSignature) {
          queryParams.caseSignature = params.caseSignature;
        }

        if (params.cptCodes && params.cptCodes.length > 0) {
          queryParams.cptCodes = params.cptCodes.join(",");
        }

        if (params.payerId !== undefined) {
          queryParams.payerId = params.payerId.toString();
        }

        if (params.accountId) {
          queryParams.accountId = params.accountId;
        }

        return {
          url: "/implant-suggestions",
          params: queryParams,
        };
      },
    }),
  }),
});

export const {
  useGetImplantSuggestionQuery,
  useLazyGetImplantSuggestionQuery,
} = implantSuggestionsQueries;
